<template>
  <div
    v-if="hasHelper || $slots.default"
    class="FieldHelper"
    :class="{
      'is-error': Boolean(errorMessage)
    }"
  >
    <Icon :icon="iconName" :size="14" class="FieldHelper-icon" />
    <span class="FieldHelper-text">
      {{ errorMessage || helperText }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Icon from '../Icon/Icon.vue';
import type { IconName } from '../Icon/Icon.model';

export interface FieldHelperProps {
  /** Error message to display (takes priority over helperText) */
  errorMessage?: string;
  /** Helper text to display when there's no error */
  helperText?: string;
  /** Custom icon name - defaults to alert-circle for errors, information for helper */
  icon?: IconName;
}

const props = defineProps<FieldHelperProps>();

const hasHelper = computed(() => Boolean(props.errorMessage || props.helperText));

const iconName = computed((): IconName => {
  if (props.icon) return props.icon;
  return props.errorMessage ? 'alert-circle' : 'information';
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .FieldHelper {
    @include text.text('xs');
    display: flex;
    align-items: center;
    gap: vars.$dimensions-d-1;
    color: vars.$atoms-input-enabled-helper-color;

    &-icon {
      flex-shrink: 0;
    }

    &-text {
      flex: 1;
    }

    &.is-error {
      color: vars.$atoms-input-error-helper-color;
    }
  }
}
</style>